import { useState, useEffect, useCallback } from 'react'
import { PageHeader, DataTable, StatusBadge } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'
import { useApiList } from '../../hooks/use-api-list'

interface MigrationRun {
  readonly id: string
  readonly organizationId: string
  readonly environmentId: string
  readonly kind: string
  readonly status: string
  readonly createdAt: string
  readonly startedAt: string | null
  readonly completedAt: string | null
}

interface MigrationProgressRow {
  readonly entityType: string
  readonly totalLegacy: number
  readonly migratedOk: number
  readonly migratedSkipped: number
  readonly migratedFailed: number
}

interface LegacyUserRow {
  readonly id: string
  readonly legacyUserId: string
  readonly legacyEmail: string | null
  readonly legacyPhone: string | null
  readonly legacyRole: string
  readonly legacyStatus: string
  readonly matchStatus: 'unmatched' | 'matched' | 'dismissed'
  readonly matchedEdgeUserId: string | null
}

type TabKey = 'runs' | 'progress' | 'legacy-users'

const runColumns: readonly Column<MigrationRun>[] = [
  { key: 'kind', header: 'Kind', render: (r) => r.kind, sortable: true },
  {
    key: 'status',
    header: 'Status',
    render: (r) => (
      <StatusBadge status={r.status === 'ok' ? 'active' : r.status === 'failed' ? 'error' : 'info'} label={r.status} />
    ),
  },
  { key: 'createdAt', header: 'Created', render: (r) => new Date(r.createdAt).toLocaleString() },
  {
    key: 'completedAt',
    header: 'Completed',
    render: (r) => (r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'),
  },
]

const legacyUserColumns: readonly Column<LegacyUserRow>[] = [
  { key: 'legacyEmail', header: 'Email', render: (r) => r.legacyEmail ?? '—' },
  { key: 'legacyPhone', header: 'Phone', render: (r) => r.legacyPhone ?? '—' },
  { key: 'legacyRole', header: 'Role', render: (r) => r.legacyRole },
  { key: 'legacyStatus', header: 'Status', render: (r) => r.legacyStatus },
  {
    key: 'matchStatus',
    header: 'Match',
    render: (r) => (
      <StatusBadge
        status={r.matchStatus === 'matched' ? 'active' : r.matchStatus === 'dismissed' ? 'warning' : 'error'}
        label={r.matchStatus}
      />
    ),
  },
]

function ProgressBar({ ok, skipped, failed, total }: { ok: number; skipped: number; failed: number; total: number }) {
  if (total === 0) return <span className="text-gray-400">—</span>
  const pct = Math.round(((ok + skipped) / total) * 100)
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
        <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-600 w-10 text-right">{pct}%</span>
      {failed > 0 && <span className="text-xs text-red-600">({failed} err)</span>}
    </div>
  )
}

export function MigrationDashboardPage() {
  const { user } = useAuth()
  const isMedflowAdmin = user?.role === 'medflow_admin'
  const [activeTab, setActiveTab] = useState<TabKey>('runs')
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [progress, setProgress] = useState<readonly MigrationProgressRow[]>([])
  const [progressLoading, setProgressLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [matchFilter, setMatchFilter] = useState<string | undefined>(undefined)

  const runs = useApiList<MigrationRun>({ path: '/admin/migration/runs' })
  const legacyUsers = useApiList<LegacyUserRow>({
    path: '/admin/migration/legacy-users',
    filters: { matchStatus: matchFilter },
  })

  const fetchProgress = useCallback(async (runId: string) => {
    setProgressLoading(true)
    try {
      const res = await api.get<readonly MigrationProgressRow[]>(`/admin/migration/runs/${runId}/progress`)
      setProgress(res.data ?? [])
    } catch {
      setProgress([])
    } finally {
      setProgressLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedRunId) {
      fetchProgress(selectedRunId)
    }
  }, [selectedRunId, fetchProgress])

  const handleRunNow = useCallback(async () => {
    if (!isMedflowAdmin) return
    setCreating(true)
    try {
      await api.post('/admin/migration/runs', { kind: 'backfill' })
      runs.refresh()
    } catch {
      // errors handled via UI feedback
    } finally {
      setCreating(false)
    }
  }, [isMedflowAdmin, runs])

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'runs', label: 'Runs' },
    { key: 'progress', label: 'Progress' },
    { key: 'legacy-users', label: 'Legacy Users' },
  ]

  return (
    <div>
      <PageHeader
        title="Migration Dashboard"
        actions={
          isMedflowAdmin ? (
            <button onClick={handleRunNow} disabled={creating} className="btn-primary text-sm px-3 py-1.5 rounded">
              {creating ? 'Creating...' : 'Run Now'}
            </button>
          ) : undefined
        }
      />

      <div className="flex gap-1 mb-4 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.key === 'legacy-users' && legacyUsers.total > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                {legacyUsers.total}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'runs' && (
        <div>
          {runs.loading ? (
            <p className="text-gray-500">Loading runs...</p>
          ) : runs.error ? (
            <p className="text-red-600">{runs.error}</p>
          ) : runs.data.length === 0 ? (
            <p className="text-gray-500">No migration runs yet.</p>
          ) : (
            <DataTable<MigrationRun>
              columns={runColumns}
              rows={runs.data as MigrationRun[]}
              keyField="id"
              onRowClick={(row) => {
                setSelectedRunId(row.id)
                setActiveTab('progress')
              }}
            />
          )}
        </div>
      )}

      {activeTab === 'progress' && (
        <div>
          {!selectedRunId ? (
            <p className="text-gray-500">Select a run from the Runs tab to view progress.</p>
          ) : progressLoading ? (
            <p className="text-gray-500">Loading progress...</p>
          ) : progress.length === 0 ? (
            <p className="text-gray-500">No progress data for this run.</p>
          ) : (
            <div className="space-y-3">
              {progress.map((row) => (
                <div key={row.entityType} className="border rounded p-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-sm">{row.entityType}</span>
                    <span className="text-xs text-gray-500">
                      {row.migratedOk + row.migratedSkipped} / {row.totalLegacy}
                    </span>
                  </div>
                  <ProgressBar
                    ok={row.migratedOk}
                    skipped={row.migratedSkipped}
                    failed={row.migratedFailed}
                    total={row.totalLegacy}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'legacy-users' && (
        <div>
          <div className="mb-3 flex gap-2">
            {(['all', 'unmatched', 'matched', 'dismissed'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setMatchFilter(f === 'all' ? undefined : f)}
                className={`px-3 py-1 text-xs rounded border ${
                  (f === 'all' && !matchFilter) || matchFilter === f
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-600'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {legacyUsers.loading ? (
            <p className="text-gray-500">Loading legacy users...</p>
          ) : legacyUsers.error ? (
            <p className="text-red-600">{legacyUsers.error}</p>
          ) : legacyUsers.data.length === 0 ? (
            <p className="text-gray-500">No legacy users to reconcile.</p>
          ) : (
            <DataTable<LegacyUserRow>
              columns={legacyUserColumns}
              rows={legacyUsers.data as LegacyUserRow[]}
              keyField="id"
            />
          )}
        </div>
      )}
    </div>
  )
}
